import React, { useState } from 'react';
import { DollarSign, TrendingUp, Zap, Crown, ArrowRight, Shield, Target, AlertTriangle } from 'lucide-react';

const ProfitSimulatorEnhanced = () => {
  const [capital, setCapital] = useState(10000);
  const [riskLevel, setRiskLevel] = useState('moderate');
  const [market, setMarket] = useState('both');

  const riskProfiles = {
    conservative: {
      multiplier: 0.7, label: 'Conservative', returnRange: '10-20%', icon: Shield,
      text: 'text-blue-400',
      selected: 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
    },
    moderate: {
      multiplier: 1, label: 'Moderate', returnRange: '20-35%', icon: Target,
      text: 'text-emerald-400',
      selected: 'bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border-2 border-emerald-500/50 shadow-lg shadow-emerald-500/20'
    },
    aggressive: {
      multiplier: 1.3, label: 'Aggressive', returnRange: '35-55%', icon: TrendingUp,
      text: 'text-orange-400',
      selected: 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-2 border-orange-500/50 shadow-lg shadow-orange-500/20'
    }
  };

  const markets = {
    forex: { label: 'Forex Only', bonus: 1, icon: '💱' },
    crypto: { label: 'Crypto Only', bonus: 1.1, icon: '₿' },
    both: { label: 'Forex + Crypto', bonus: 1.2, icon: '🌐' }
  };

  const packages = [
    {
      id: 1,
      name: 'Basic',
      icon: Zap,
      baseReturn: 15,
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-500/50',
      iconBg: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-blue-400',
      features: ['1 Gold Bot', 'Basic Strategy', 'Email Support', 'Monthly Reports'],
      recommended: false
    },
    {
      id: 2,
      name: 'Intermediate',
      icon: TrendingUp,
      baseReturn: 30,
      color: 'from-emerald-500 to-cyan-500',
      borderColor: 'border-emerald-500/50',
      iconBg: 'from-emerald-500/20 to-cyan-500/20',
      iconColor: 'text-emerald-400',
      features: ['3 Gold Bots', 'Advanced Strategy', 'Priority Support', 'Weekly Reports'],
      recommended: true
    },
    {
      id: 3,
      name: 'Premium',
      icon: Crown,
      baseReturn: 55,
      color: 'from-yellow-500 to-orange-500',
      borderColor: 'border-yellow-500/50',
      iconBg: 'from-yellow-500/20 to-orange-500/20',
      iconColor: 'text-yellow-400',
      features: ['Unlimited Bots', 'Premium Strategy', '24/7 VIP Support', 'Real-time Analytics'],
      recommended: false
    }
  ];

  const calculateProfit = (baseReturn) => {
    const risk = riskProfiles[riskLevel];
    const marketBonus = markets[market].bonus;
    const adjustedReturn = (baseReturn * risk.multiplier * marketBonus) / 100;
    return capital * adjustedReturn;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const capitalPresets = [500, 1000, 5000, 10000, 25000, 50000, 100000];

  const currentRisk = riskProfiles[riskLevel];
  const RiskIcon = currentRisk.icon;

  return (
    <section id="profit-simulator" className="relative bg-gradient-to-b from-black via-slate-950 to-black py-24 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Glow Effects */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full shadow-lg shadow-emerald-500/20 mb-6">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-semibold tracking-wide">ADVANCED CALCULATOR</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            Calculate Your{' '}
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              AI Profit Potential
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Customize your trading parameters and see realistic monthly returns based on your capital, risk tolerance, and market preference.
          </p>
        </div>

        {/* Advanced Calculator Card */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-950/80 backdrop-blur-xl border border-slate-800/50 rounded-3xl p-8 lg:p-12 shadow-2xl">
            
            {/* Capital Input */}
            <div className="mb-12">
              <div className="text-center mb-8">
                <label className="text-slate-400 text-sm font-semibold mb-4 block uppercase tracking-wider">
                  Your Trading Capital
                </label>
                <div data-testid="capital-display" className="text-5xl sm:text-6xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {formatCurrency(capital)}
                </div>
                <p className="text-slate-500 text-sm">Adjust slider or select preset amount</p>
              </div>

              {/* Slider */}
              <div className="mb-6">
                <input
                  data-testid="capital-slider"
                  type="range"
                  min="500"
                  max="100000"
                  step="500"
                  value={capital}
                  onChange={(e) => setCapital(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                  style={{
                    background: `linear-gradient(to right, rgb(16 185 129) 0%, rgb(16 185 129) ${((capital - 500) / (100000 - 500)) * 100}%, rgb(51 65 85) ${((capital - 500) / (100000 - 500)) * 100}%, rgb(51 65 85) 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>$500</span>
                  <span>$100,000</span>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="flex flex-wrap gap-2 justify-center">
                {capitalPresets.map((preset) => (
                  <button
                    key={preset}
                    data-testid={`capital-preset-${preset}`}
                    onClick={() => setCapital(preset)}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${
                      capital === preset
                        ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-black shadow-lg shadow-emerald-500/50'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800 border border-slate-700/50'
                    }`}
                  >
                    {formatCurrency(preset)}
                  </button>
                ))}
              </div>
            </div>

            {/* Risk Level Selector */}
            <div className="mb-12">
              <label className="text-slate-300 text-lg font-bold mb-6 block text-center">
                Select Your Risk Tolerance
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(riskProfiles).map(([key, profile]) => {
                  const Icon = profile.icon;
                  const isSelected = riskLevel === key;
                  return (
                    <button
                      key={key}
                      data-testid={`risk-${key}-btn`}
                      onClick={() => setRiskLevel(key)}
                      className={`p-6 rounded-2xl transition-all duration-300 ${
                        isSelected
                          ? profile.selected
                          : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <Icon className={`w-10 h-10 mb-3 ${isSelected ? profile.text : 'text-slate-400'}`} />
                        <div className={`text-lg font-bold mb-2 ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {profile.label}
                        </div>
                        <div className={`text-sm ${isSelected ? profile.text : 'text-slate-500'}`}>
                          {profile.returnRange} monthly
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Market Selector */}
            <div className="mb-12">
              <label className="text-slate-300 text-lg font-bold mb-6 block text-center">
                Choose Your Market
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(markets).map(([key, mkt]) => {
                  const isSelected = market === key;
                  return (
                    <button
                      key={key}
                      data-testid={`market-${key}-btn`}
                      onClick={() => setMarket(key)}
                      className={`p-6 rounded-2xl transition-all duration-300 ${
                        isSelected
                          ? 'bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20'
                          : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="text-4xl mb-3">{mkt.icon}</div>
                        <div className={`text-lg font-bold mb-2 ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {mkt.label}
                        </div>
                        {mkt.bonus > 1 && (
                          <div className="text-xs text-emerald-400 font-semibold">
                            +{((mkt.bonus - 1) * 100).toFixed(0)}% bonus
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results Summary */}
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 rounded-2xl p-8 mb-8 border border-slate-700/50">
              <div className="flex items-center justify-center gap-3 mb-4">
                <RiskIcon className={`w-6 h-6 ${currentRisk.text}`} />
                <h3 className="text-xl font-bold text-white">Your Configuration</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center" data-testid="simulator-config-summary">
                <div>
                  <div className="text-slate-400 text-sm mb-1">Risk Level</div>
                  <div className={`text-lg font-bold ${currentRisk.text}`} data-testid="summary-risk">{currentRisk.label}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm mb-1">Market</div>
                  <div className="text-lg font-bold text-cyan-400" data-testid="summary-market">{markets[market].label}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm mb-1">Expected Range</div>
                  <div className="text-lg font-bold text-emerald-400" data-testid="summary-range">{currentRisk.returnRange}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Package Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {packages.map((pkg) => {
            const IconComponent = pkg.icon;
            const profit = calculateProfit(pkg.baseReturn);
            const totalReturn = capital + profit;
            const returnPercent = (profit / capital) * 100;

            return (
              <div
                key={pkg.id}
                data-testid={`package-card-${pkg.name.toLowerCase()}`}
                className={`relative bg-gradient-to-br from-slate-900/80 to-slate-950/80 backdrop-blur-xl border ${pkg.borderColor} rounded-3xl p-8 transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                  pkg.recommended ? 'ring-2 ring-emerald-500/50 shadow-xl shadow-emerald-500/20 md:-mt-4' : ''
                }`}
              >
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full shadow-xl text-black font-bold text-sm">
                      <Crown className="w-4 h-4" />
                      RECOMMENDED
                    </div>
                  </div>
                )}

                {/* Icon */}
                <div className={`w-20 h-20 bg-gradient-to-br ${pkg.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <IconComponent className={`w-10 h-10 ${pkg.iconColor}`} />
                </div>

                {/* Package Name */}
                <h3 className="text-3xl font-black text-white text-center mb-6">{pkg.name}</h3>

                {/* Return Percentage */}
                <div className="text-center mb-6 p-6 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <div className={`text-5xl font-black bg-gradient-to-r ${pkg.color} bg-clip-text text-transparent mb-2`}>
                    {returnPercent.toFixed(1)}%
                  </div>
                  <div className="text-slate-400 text-sm">Monthly Returns</div>
                </div>

                {/* Profit Breakdown */}
                <div className="space-y-3 mb-8">
                  <div className="flex justify-between items-center p-4 bg-slate-800/50 rounded-lg">
                    <span className="text-slate-400 text-sm">Your Capital</span>
                    <span className="text-white font-bold">{formatCurrency(capital)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-lg">
                    <span className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Monthly Profit
                    </span>
                    <span className="text-emerald-400 font-black text-xl" data-testid={`package-profit-${pkg.name.toLowerCase()}`}>+{formatCurrency(profit)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg">
                    <span className="text-cyan-400 text-sm font-semibold">Total Value</span>
                    <span className="text-cyan-400 font-black text-xl">{formatCurrency(totalReturn)}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  data-testid={`package-cta-${pkg.name.toLowerCase()}`}
                  onClick={() => {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`w-full py-4 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
                    pkg.recommended
                      ? `bg-gradient-to-r ${pkg.color} text-black shadow-lg hover:shadow-xl`
                      : 'bg-slate-800/50 text-white hover:bg-slate-800 border border-slate-700/50'
                  }`}
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-orange-900/20 to-red-900/20 border border-orange-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-orange-400 font-bold mb-2">Risk Disclosure</h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Trading involves substantial risk of loss. Past performance does not guarantee future results. These calculations are projections based on historical data and actual returns may vary significantly based on market conditions, trading execution, and individual circumstances.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(16 185 129), rgb(6 182 212));
          cursor: pointer;
          box-shadow: 0 0 15px rgba(16, 185, 129, 0.6);
        }

        .slider-thumb::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(16 185 129), rgb(6 182 212));
          cursor: pointer;
          border: none;
          box-shadow: 0 0 15px rgba(16, 185, 129, 0.6);
        }
      `}</style>
    </section>
  );
};

export default ProfitSimulatorEnhanced;
